import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ApprovalAssignment,
  ApprovalAssignmentScope,
  SaveApprovalAssignmentRequest,
  ShiftProductionFormListItem,
  ApprovalMailStatus,
} from "../../../shared/shift-production-form/contracts";
import {
  getApprovalAssignments,
  getCorporateUsers,
  getApprovalMailStatuses,
  getShiftFormList,
  issueApproverPin,
  removeApprovalAssignment,
  saveApprovalAssignment,
} from "../shift-production-form/shift-production-form-gateway";

export function useApprovalAssignments() {
  const [assignments, setAssignments] = useState<{
    defaults: ApprovalAssignment[];
    lineOverrides: ApprovalAssignment[];
  }>({ defaults: [], lineOverrides: [] });
  const [users, setUsers] = useState<Awaited<ReturnType<typeof getCorporateUsers>>["items"]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [unassigned, setUnassigned] = useState<{
    items: ShiftProductionFormListItem[];
    total: number;
  }>({ items: [], total: 0 });
  const [mailStatuses, setMailStatuses] = useState<ApprovalMailStatus[]>([]);
  const [issuedPin, setIssuedPin] = useState<{ userId: string; name: string; pin: string } | null>(null);
  const reloadRequest = useRef(0);
  const mutationRequest = useRef(0);

  const reload = useCallback(async () => {
    const request = ++reloadRequest.current;
    setPhase("loading");
    setError(null);
    setIssuedPin(null);
    try {
      const [nextAssignments, nextUsers, nextUnassigned, nextMailStatuses] = await Promise.all([
        getApprovalAssignments(),
        getCorporateUsers(),
        getShiftFormList("unassigned", { limit: 10 }),
        getApprovalMailStatuses(),
      ]);
      if (request !== reloadRequest.current) return;
      setAssignments(nextAssignments);
      setUsers(nextUsers.items.filter((user) => user.active));
      setUnassigned(nextUnassigned);
      setMailStatuses(nextMailStatuses.items);
      setPhase("ready");
    } catch (reason) {
      if (request !== reloadRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Onay atamaları yüklenemedi.");
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => () => {
    reloadRequest.current += 1;
    mutationRequest.current += 1;
  }, []);

  const assignmentFor = useCallback((scope: ApprovalAssignmentScope) => {
    const source = scope.scopeType === "DEPARTMENT"
      ? assignments.defaults
      : assignments.lineOverrides;
    return source.find((item) =>
      item.scope.scopeType === scope.scopeType
      && item.scope.departmentName === scope.departmentName
      && item.scope.lineName === scope.lineName,
    ) ?? null;
  }, [assignments]);

  const save = async (input: SaveApprovalAssignmentRequest) => {
    if (busy) return;
    const request = ++mutationRequest.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await saveApprovalAssignment(input);
      if (request !== mutationRequest.current) return;
      setAssignments((current) => {
        const key = (item: ApprovalAssignment) => `${item.scope.scopeType}:${item.scope.departmentName}:${item.scope.lineName ?? ""}`;
        const nextKey = key(result.item);
        const source = result.item.scope.scopeType === "DEPARTMENT" ? current.defaults : current.lineOverrides;
        const next = [...source.filter((item) => key(item) !== nextKey), result.item];
        return result.item.scope.scopeType === "DEPARTMENT"
          ? { ...current, defaults: next }
          : { ...current, lineOverrides: next };
      });
      setNotice(result.transferredFormCount > 0
        ? `${result.transferredFormCount} bekleyen form yeni atamaya devredildi.`
        : "Onay ataması kaydedildi.");
    } catch (reason) {
      if (request !== mutationRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Onay ataması kaydedilemedi.");
    } finally {
      if (request === mutationRequest.current) setBusy(false);
    }
  };

  const remove = async (
    scope: Extract<ApprovalAssignmentScope, { scopeType: "LINE" }>,
    expectedVersion: number,
    transferPendingForms: boolean,
  ) => {
    if (busy) return;
    const request = ++mutationRequest.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await removeApprovalAssignment({
        scope,
        expectedVersion,
        transferPendingForms,
      });
      if (request !== mutationRequest.current) return;
      setAssignments((current) => ({
        ...current,
        lineOverrides: current.lineOverrides.filter((item) => item.id !== assignmentFor(scope)?.id),
      }));
      setNotice(result.transferredFormCount > 0
        ? `${result.transferredFormCount} bekleyen form bölüm varsayımına devredildi.`
        : "Makineye özel atama kaldırıldı.");
    } catch (reason) {
      if (request !== mutationRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Makineye özel atama kaldırılamadı.");
    } finally {
      if (request === mutationRequest.current) setBusy(false);
    }
  };

  const issuePin = async (userId: string) => {
    if (busy) return;
    const request = ++mutationRequest.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    setIssuedPin(null);
    try {
      const displayed = users.find((user) => user.id === userId);
      if (!displayed) throw new Error("Onaycı kaydı bulunamadı. Listeyi yenileyin.");
      const result = await issueApproverPin(userId, displayed.approverPinChangedAt);
      if (request !== mutationRequest.current) return;
      setUsers((current) => current.map((user) => user.id === result.user.id ? result.user : user));
      setIssuedPin({ userId: result.user.id, name: result.user.name, pin: result.pin });
    } catch (reason) {
      if (request !== mutationRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Onaycı PIN'i oluşturulamadı.");
    } finally {
      if (request === mutationRequest.current) setBusy(false);
    }
  };

  return {
    assignments,
    users,
    phase,
    busy,
    error,
    notice,
    unassigned,
    mailStatuses,
    issuedPin,
    reload,
    assignmentFor,
    save,
    remove,
    issuePin,
    clearIssuedPin: () => setIssuedPin(null),
  };
}
